import LinkButton from '@components/utils/cta/linkButton';
import NewTabLink from '@components/utils/links/newTabLink';
import { ADDITIONAL_PRODUCT } from '@constants';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import {
    AdditionalProductFragment,
    RedirectionType,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useAddToCart, useHasAddToCart } from '@hooks';
import { Button, HStack, IconButton } from '@lib/uikit';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

type PropsType = {
    additionalProduct: AdditionalProductFragment;
    selectedVariant: VariantFragment | null;
    context: OutboundLinkContext;
};

export const AdditionalProductActions = ({
    additionalProduct,
    selectedVariant,
    context,
}: PropsType) => {
    const { t } = useTranslation(ADDITIONAL_PRODUCT, { keyPrefix: 'actions' });
    const { hasAddToCart } = useHasAddToCart(additionalProduct);
    const { isAddingToCart, addToCart } = useAddToCart({
        context,
        additionalProduct,
        variant: selectedVariant,
    });

    const link = useMemo(
        () => ({
            url: selectedVariant?.Url || additionalProduct.Url,
            name: selectedVariant?.Name || additionalProduct.Name,
        }),

        [additionalProduct, selectedVariant]
    );

    if (hasAddToCart) {
        return (
            <HStack spacing='xs'>
                <Button
                    isFullWidth
                    disabled={!selectedVariant}
                    isLoading={isAddingToCart}
                    onClick={addToCart}
                >
                    {t('add_to_cart.label')}
                </Button>
                {link.url && (
                    <IconButton
                        as={NewTabLink}
                        ariaLabel='See'
                        icon='open_in_new'
                        color='black'
                        variant='outline'
                        href={link.url}
                        destination_name={link.name}
                        destination_category={
                            OutboundLinkDestinationCategory.AdditionalProduct
                        }
                        context={context}
                        shape={OutboundLinkShape.IconButton}
                    />
                )}
            </HStack>
        );
    }

    if (link.url) {
        return (
            <LinkButton
                isFullWidth
                text={t('see')}
                type={RedirectionType.Outside}
                url={link.url}
                destinationName={link.name}
                destinationCategory={
                    OutboundLinkDestinationCategory.AdditionalProduct
                }
                context={context}
                shape={OutboundLinkShape.Button}
                rightIcon='open_in_new'
            />
        );
    }

    return null;
};
